/**
 * Calculo de distancias sobre la superficie terrestre.
 *
 * La misma funcion se usa en el cliente (para decidir cuando entra en radio) y
 * en el servidor (para validar que la visita es plausible). Tienen que dar
 * exactamente el mismo resultado, por eso vive en el paquete compartido y no
 * duplicada en cada lado.
 */

/** Radio medio terrestre en metros, segun el elipsoide WGS 84. */
const RADIO_TERRESTRE_METROS = 6_371_008.8;

export interface Coordenada {
  lat: number;
  lon: number;
}

const gradosARadianes = (grados: number): number => (grados * Math.PI) / 180;

/**
 * Distancia haversine en metros entre dos coordenadas.
 *
 * Precision mas que suficiente para distancias de gymkhana (decenas o cientos
 * de metros). No corrige el achatamiento terrestre, que a esta escala introduce
 * un error muy por debajo del error del propio GPS.
 */
export function distanciaEnMetros(a: Coordenada, b: Coordenada): number {
  const latA = gradosARadianes(a.lat);
  const latB = gradosARadianes(b.lat);
  const incrementoLat = gradosARadianes(b.lat - a.lat);
  const incrementoLon = gradosARadianes(b.lon - a.lon);

  const h =
    Math.sin(incrementoLat / 2) ** 2 +
    Math.cos(latA) * Math.cos(latB) * Math.sin(incrementoLon / 2) ** 2;

  return 2 * RADIO_TERRESTRE_METROS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Comprueba si una coordenada cae dentro del radio de un punto. */
export function dentroDeRadio(
  posicion: Coordenada,
  punto: Coordenada,
  radioMetros: number,
): boolean {
  return distanciaEnMetros(posicion, punto) <= radioMetros;
}

/**
 * Valida que unas coordenadas son geograficamente posibles. Sirve tanto para
 * rechazar cuerpos de peticion manipulados como para descartar lecturas
 * absurdas del GPS antes de usarlas.
 */
export function coordenadaValida(valor: Coordenada): boolean {
  return (
    Number.isFinite(valor.lat) &&
    Number.isFinite(valor.lon) &&
    valor.lat >= -90 &&
    valor.lat <= 90 &&
    valor.lon >= -180 &&
    valor.lon <= 180
  );
}

/**
 * Formatea una distancia para mostrarla, usando la API de internacionalizacion
 * para que el separador decimal y la unidad respeten el idioma activo.
 */
export function formatearDistancia(metros: number, etiquetaBcp47: string): string {
  if (metros < 1000) {
    return new Intl.NumberFormat(etiquetaBcp47, {
      style: 'unit',
      unit: 'meter',
      unitDisplay: 'short',
      maximumFractionDigits: 0,
    }).format(Math.round(metros));
  }
  return new Intl.NumberFormat(etiquetaBcp47, {
    style: 'unit',
    unit: 'kilometer',
    unitDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(metros / 1000);
}
